/** Service Desk plan features, limits and add-ons: mirror of backend `app/core/constants/plans.py`.
 *  Generated once from the backend constants; `tests/core/test_service_desk_mirror.py` keeps the two equal. */

export const PLAN_FEATURE_VALUES = [
  "widget",
  "webapp",
  "messenger",
  "api",
  "actions",
  "helpdesk",
  "mobile_white_label",
  "sso",
  "evaluations",
  "remove_branding",
  "custom_roles",
  "private_deployment",
  "sla",
  "customer_success_manager",
  "workflows",
  "after_sales",
  "commerce",
  "commerce_presets",
  "connectors",
  "graph_retrieval",
  "secure_launch",
  "secure_launch_facts",
] as const;
export type PlanFeature = (typeof PLAN_FEATURE_VALUES)[number];

export const ADDON_KEY_VALUES = [
  "message_pack",
  "extra_agent",
  "extra_seat",
  "remove_branding",
  "white_label_mobile_setup",
  "attachment_storage_25gb",
  "extra_connector",
  "field_seat",
  "after_sales_pack",
  "knowledge_storage_1gb",
  "secure_launch",
  "secure_launch_lite",
] as const;
export type AddonKey = (typeof ADDON_KEY_VALUES)[number];

export const SERVICE_LIMIT_VALUES = [
  "organisations",
  "contacts",
  "custom_ticket_types",
  "custom_ticket_fields",
  "custom_ticket_statuses",
  "ticket_sla_policies",
  "routing_rules",
  "connectors",
  "connector_operations",
  "connector_calls_month",
  "commerce_profiles",
  "catalogue_items",
  "tickets_month",
  "attachment_storage_mb",
  "field_seats",
] as const;
export type ServiceLimit = (typeof SERVICE_LIMIT_VALUES)[number];

export const TICKETS_API_ACCESS_VALUES = ["none", "read", "write"] as const;
export type TicketsApiAccess = (typeof TICKETS_API_ACCESS_VALUES)[number];

export const SERVICE_DESK_PLAN_LIMITS = {
  starter: {
    organisations: 0,
    contacts: 0,
    customTicketTypes: 0,
    customTicketFields: 0,
    customTicketStatuses: 0,
    ticketSlaPolicies: 0,
    routingRules: 0,
    connectors: 0,
    connectorOperations: 0,
    connectorCallsMonth: 0,
    commerceProfiles: 0,
    catalogueItems: 0,
    ticketsMonth: 0,
    attachmentStorageMb: 0,
    fieldSeats: 0,
    slaCalendars: false,
    contractSlas: false,
    skillsRouting: false,
    connectorSyncJobs: false,
    connectorInboundHooks: false,
    ticketsApi: "none",
  },
  standard: {
    organisations: 300,
    contacts: 1500,
    customTicketTypes: 5,
    customTicketFields: 30,
    customTicketStatuses: 10,
    ticketSlaPolicies: 1,
    routingRules: 20,
    connectors: 1,
    connectorOperations: 10,
    connectorCallsMonth: 10000,
    commerceProfiles: 0,
    catalogueItems: 0,
    ticketsMonth: 1500,
    attachmentStorageMb: 5120,
    fieldSeats: 0,
    slaCalendars: false,
    contractSlas: false,
    skillsRouting: false,
    connectorSyncJobs: false,
    connectorInboundHooks: false,
    ticketsApi: "read",
  },
  pro: {
    organisations: 3000,
    contacts: 20000,
    customTicketTypes: 30,
    customTicketFields: 150,
    customTicketStatuses: 30,
    ticketSlaPolicies: null,
    routingRules: 100,
    connectors: 5,
    connectorOperations: 50,
    connectorCallsMonth: 100000,
    commerceProfiles: 1,
    catalogueItems: 20000,
    ticketsMonth: 10000,
    attachmentStorageMb: 25600,
    fieldSeats: 0,
    slaCalendars: true,
    contractSlas: true,
    skillsRouting: true,
    connectorSyncJobs: true,
    connectorInboundHooks: true,
    ticketsApi: "write",
  },
  institution: {
    organisations: null,
    contacts: null,
    customTicketTypes: null,
    customTicketFields: null,
    customTicketStatuses: null,
    ticketSlaPolicies: null,
    routingRules: null,
    connectors: 20,
    connectorOperations: null,
    connectorCallsMonth: null,
    commerceProfiles: 5,
    catalogueItems: 200000,
    ticketsMonth: null,
    attachmentStorageMb: null,
    fieldSeats: 0,
    slaCalendars: true,
    contractSlas: true,
    skillsRouting: true,
    connectorSyncJobs: true,
    connectorInboundHooks: true,
    ticketsApi: "write",
  },
} as const;

export const PLAN_FEATURES = {
  starter: ["webapp", "widget"],
  standard: ["actions", "api", "connectors", "helpdesk", "messenger", "webapp", "widget", "workflows"],
  pro: [
    "actions",
    "after_sales",
    "api",
    "commerce",
    "connectors",
    "custom_roles",
    "evaluations",
    "graph_retrieval",
    "helpdesk",
    "messenger",
    "mobile_white_label",
    "remove_branding",
    "sso",
    "webapp",
    "widget",
    "workflows",
  ],
  institution: [
    "actions",
    "after_sales",
    "api",
    "commerce",
    "commerce_presets",
    "connectors",
    "custom_roles",
    "customer_success_manager",
    "evaluations",
    "graph_retrieval",
    "helpdesk",
    "messenger",
    "mobile_white_label",
    "private_deployment",
    "remove_branding",
    "secure_launch",
    "secure_launch_facts",
    "sla",
    "sso",
    "webapp",
    "widget",
    "workflows",
  ],
} as const;

export const PLAN_FEATURE_MIN_PLAN = {
  widget: "starter",
  webapp: "starter",
  messenger: "standard",
  api: "standard",
  actions: "standard",
  helpdesk: "standard",
  mobile_white_label: "pro",
  evaluations: "pro",
  remove_branding: "pro",
  custom_roles: "pro",
  sso: "pro",
  private_deployment: "institution",
  sla: "institution",
  customer_success_manager: "institution",
  workflows: "standard",
  after_sales: "pro",
  commerce: "pro",
  commerce_presets: "institution",
  connectors: "standard",
  graph_retrieval: "pro",
  secure_launch: "institution",
  secure_launch_facts: "institution",
} as const;

export const SERVICE_NAV_REQUIREMENTS = {
  tickets: { permission: "tickets.view", feature: "helpdesk" },
  organisations: { permission: "organisations.view", feature: "helpdesk" },
  contacts: { permission: "organisations.view", feature: "helpdesk" },
  assets: { permission: "assets.view", feature: "after_sales" },
  installations: { permission: "assets.view", feature: "after_sales" },
  visits: { permission: "visits.view", feature: "after_sales" },
  orders: { permission: "orders.view", feature: "commerce" },
  catalogue: { permission: "orders.view", feature: "commerce" },
  "service-reports": { permission: "tickets.reports", feature: "helpdesk" },
  "service-settings": { permission: "tickets.configure", feature: "helpdesk" },
} as const;

export const ADDON_PRICES_PHP = {
  message_pack: 1500,
  extra_agent: 900,
  extra_seat: 450,
  remove_branding: 2000,
  white_label_mobile_setup: 15000,
  attachment_storage_25gb: 1500,
  extra_connector: 2500,
  field_seat: 250,
  after_sales_pack: 4900,
  knowledge_storage_1gb: 1500,
  secure_launch: 5000,
  secure_launch_lite: 2500,
} as const;
